import type { Agenda } from '../types/agenda';
import { itemAppliesOn } from './recurrence';

/** Parse 'H:MM' / 'HH:MM' to minutes since midnight, or null if invalid. */
export function parseTimeToMinutes(s: string): number | null {
  if (!s) return null;
  const parts = s.split(':');
  if (parts.length !== 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m) || m < 0 || m >= 60 || h < 0) return null;
  return h * 60 + m;
}

/** Format minutes since midnight as 'H:MM'. */
export function formatMinutes(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/**
 * Wall-clock start time (minutes since midnight) for each item, chaining
 * durations forward. Mirrors the Go backend's fillTimes logic:
 * - Independent items use their declared start (don't advance the clock).
 * - Non-independent items with a declared start re-anchor the clock.
 * - Non-independent items without a start inherit the current clock position.
 * agenda.meetingTime is used as the initial anchor only if parseable (H:MM).
 * Entries are null when uncomputable (clock not yet anchored, no declared start).
 *
 * Recurring items that don't apply to this meeting (off-schedule or explicitly
 * skipped) are excluded: they get a null start and do not advance the clock, so
 * the preview matches the backend's filterForPublication + fillTimes output.
 */
export function computeItemStartMinutes(agenda: Agenda): (number | null)[] {
  const items = agenda.items;
  const result: (number | null)[] = [];
  let cursor: number | null = agenda.meetingTime
    ? parseTimeToMinutes(agenda.meetingTime)
    : null;

  for (const item of items) {
    const tr = item.timeRange;
    if (!tr) {
      result.push(null);
      continue;
    }

    // A recurring item that doesn't apply this meeting is dropped from the
    // published agenda, so it must not contribute its duration to the clock.
    if (item.recurring && !itemAppliesOn(item, agenda.date)) {
      result.push(null);
      continue;
    }

    if (tr.independent) {
      result.push(tr.start ? parseTimeToMinutes(tr.start) : null);
    } else {
      if (tr.start) {
        const s = parseTimeToMinutes(tr.start);
        if (s !== null) cursor = s;
      }
      result.push(cursor);
      if (cursor !== null) cursor += tr.duration ?? 0;
    }
  }

  return result;
}
